import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import TopNavigation from '../TopNavigation';
import ClueRaiderBanner from '../components/ClueRaiderBanner';
import { blogPosts } from './blogPosts';
import MagazineBrowser from './components/MagazineBrowser';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    // Update page title and meta tags for SEO
    if (post) {
      document.title = `${post.title} - Dimension Flux`;
      
      // Update or create meta tags
      const updateMetaTag = (property: string, content: string, isProperty = true) => {
        const attribute = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attribute}="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attribute, property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      const url = `https://dimensionflux.com/blog/${post.slug}`;
      const imageUrl = post.image ? `https://dimensionflux.com${post.image}` : 'https://dimensionflux.com/res.png?v=5';

      // Open Graph tags
      updateMetaTag('og:title', post.title);
      updateMetaTag('og:description', post.excerpt);
      updateMetaTag('og:image', imageUrl);
      updateMetaTag('og:url', url);
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:site_name', 'Dimension Flux');
      updateMetaTag('og:updated_time', new Date(post.date).toISOString());

      // Twitter Card tags
      updateMetaTag('twitter:card', 'summary_large_image', false);
      updateMetaTag('twitter:title', post.title, false);
      updateMetaTag('twitter:description', post.excerpt, false);
      updateMetaTag('twitter:image', imageUrl, false);
      updateMetaTag('twitter:site', '@dimensionflux', false);
      updateMetaTag('twitter:creator', '@dimensionflux', false);

      // Standard meta description
      updateMetaTag('description', post.excerpt, false);

      // Article structured data
      const existingScript = document.getElementById('article-structured-data');
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement('script');
      script.id = 'article-structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: imageUrl,
        datePublished: post.date,
        dateModified: post.date,
        author: {
          '@type': 'Person',
          name: post.author
        },
        publisher: {
          '@type': 'Organization',
          name: 'Dimension Flux',
          logo: {
            '@type': 'ImageObject',
            url: 'https://dimensionflux.com/res.png?v=5'
          }
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url
        }
      });
      document.head.appendChild(script);
    } else {
      document.title = "Article Not Found - Dimension Flux";
    }
  }, [slug, post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <TopNavigation />
        <ClueRaiderBanner />
        <div className="p-8 max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-oswald font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-white underline">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  // If it's a special post, render the custom component
  if (post.isSpecial && post.slug === 'science-fiction-monthly-gallery') {
    return <MagazineBrowser />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <TopNavigation />
      <ClueRaiderBanner />

      <div className="pt-2 pb-2 px-2 sm:pt-2 sm:pb-4 sm:px-4">
        <div className="max-w-xl mx-auto w-full">
          <div className="bg-white bg-opacity-95 rounded-lg p-3 sm:p-6 border-2 border-gray-300 shadow-lg mb-4 sm:mb-6 w-full">
            <Link 
              to="/blog" 
              className="inline-block mb-4 text-gray-700 hover:text-gray-900 underline text-sm sm:text-base"
            >
              ← Back to Blog
            </Link>

            <article>
              <div className="flex gap-4 mb-4 items-start">
                {post.image && (
                  <div className="flex-shrink-0">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-gray-200"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-oswald font-bold mb-4 text-gray-900">
                    {post.title}
                  </h1>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-6 pb-4 border-b border-gray-300">
                {new Date(post.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} • {post.author}
              </div>

              <div className="space-y-6 text-gray-800 font-lora text-sm sm:text-base leading-relaxed">
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }: any) => (
                      <h1 className="text-2xl font-oswald font-bold mt-8 mb-4 text-gray-900" {...props}>
                        {props.children || ''}
                      </h1>
                    ),
                    h2: ({ node, ...props }: any) => (
                      <h2 className="text-xl font-oswald font-bold mt-6 mb-3 text-gray-900" {...props}>
                        {props.children || ''}
                      </h2>
                    ),
                    h3: ({ node, ...props }: any) => (
                      <h3 className="text-lg font-oswald font-semibold mt-4 mb-2 text-gray-900" {...props}>
                        {props.children || ''}
                      </h3>
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-4 text-gray-700 leading-relaxed" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="list-disc mb-4 text-gray-700 space-y-2 ml-6" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="list-decimal mb-4 text-gray-700 space-y-2 ml-6" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="text-gray-700 list-item" {...props} />
                    ),
                    code: ({ node, ...props }: any) => {
                      const isInline = !props.className;
                      if (isInline) {
                        return (
                          <code className="bg-gray-200 px-1.5 py-0.5 rounded text-gray-800 text-sm font-mono" {...props} />
                        );
                      }
                      return (
                        <code className="block bg-gray-100 p-4 rounded mb-4 overflow-x-auto text-sm font-mono border border-gray-300" {...props} />
                      );
                    },
                    pre: ({ node, ...props }) => (
                      <pre className="bg-gray-100 p-4 rounded mb-4 overflow-x-auto border border-gray-300" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote className="border-l-4 border-gray-400 pl-4 italic text-gray-600 my-4" {...props} />
                    ),
                    a: ({ node, ...props }: any) => {
                      const content = props.children || 'Link';
                      return <a className="text-gray-700 underline hover:text-gray-900" {...props}>{content}</a>;
                    },
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-gray-900" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="italic text-gray-700" {...props} />
                    ),
                    img: ({ node, ...props }: any) => (
                      <img 
                        className="max-w-full h-auto rounded-lg my-6 mx-auto shadow-md" 
                        alt={props.alt || 'Image'} 
                        {...props} 
                      />
                    ),
                  }}
                  rehypePlugins={[rehypeRaw]}
                >
                  {post.content}
                </ReactMarkdown>
              </div>
            </article>

            <div className="mt-6 text-center">
              <Link 
                to="/blog" 
                className="inline-block text-gray-700 hover:text-gray-900 underline text-sm sm:text-base"
              >
                ← Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;

